/**
 * Admin — Collection & Disposal Agencies management.
 * Super-admin can add, edit, activate/deactivate and delete agencies.
 * Tagged by state + district so the cert wizard can filter by LSGD.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Buildings, Plus, PencilSimple, Trash, X, Recycle, Factory,
  MapPin, Phone as PhoneIcon,
} from "@phosphor-icons/react";

const KIND_META = {
  collection: {
    icon: Recycle,
    label: "Collection Agencies",
    subtitle: "Who picks up waste — Rescigo, franchises, LSGDs, NGOs",
    tone: "#2D5A27",
  },
  disposal: {
    icon: Factory,
    label: "Disposal Agencies (CBWTF)",
    subtitle: "Where waste is treated — CBWTFs, recyclers, incinerators",
    tone: "#7A2E18",
  },
};

const empty = (kind) => ({
  name: "", kind, state: "Kerala", district: "",
  lsgd_name: "", lsgd_type: "panchayat",
  address: "", contact_person: "", phone: "", email: "",
  logo_url: "", license_no: "", valid_until: "",
  notes: "", active: true,
});

export default function AdminAgencies() {
  const [kind, setKind] = useState("collection");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty("collection"));
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/agencies?kind=${kind}`).then(r => setItems(r.data))
       .catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const openNew = () => { setEditing(null); setForm(empty(kind)); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty(kind), ...a }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/agencies/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/agencies", form);
        toast.success("Created");
      }
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      await api.delete(`/agencies/${a.id}?kind=${kind}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const meta = KIND_META[kind];
  const Icon = meta.icon;

  return (
    <AppShell title="Agency Directory"
              subtitle="Collection & disposal partners used by the certificate generator"
              actions={
                <button onClick={openNew} data-testid="btn-new-agency"
                        className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                  <Plus size={14} weight="bold"/>New {kind === "collection" ? "collection" : "disposal"} agency
                </button>
              }>
      <div className="flex items-center gap-2 mb-5" data-testid="agency-kind-tabs">
        {Object.entries(KIND_META).map(([k, m]) => {
          const KIcon = m.icon;
          return (
            <button key={k} onClick={() => setKind(k)}
                    data-testid={`agency-tab-${k}`}
                    className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-xs uppercase tracking-[0.18em] ${
                      kind === k ? "bg-[#2D5A27] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"
                    }`}>
              <KIcon size={13} weight="duotone"/>{m.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-md bg-white border border-[#E5E2D9] p-5 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white"
                style={{ background: meta.tone }}>
            <Icon size={18} weight="duotone"/>
          </div>
          <div>
            <div className="font-heading text-lg">{meta.label}</div>
            <div className="text-xs text-[#5C5A55]">{meta.subtitle}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid={`agency-table-${kind}`}>
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Region</th>
              <th className="px-6 py-3">Contact</th>
              {kind === "disposal" && <th className="px-6 py-3">Licence</th>}
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-[#8A8780]">
                <Buildings size={26} weight="duotone" color="#8A8780" className="mx-auto mb-2"/>
                No agencies yet.
              </td></tr>
            )}
            {items.map((a, i) => (
              <tr key={a.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`agency-row-${a.id}`}>
                <td className="px-6 py-3">
                  <div className="font-medium">{a.name}</div>
                  {a.lsgd_name && <div className="text-[11px] text-[#8A8780] mt-0.5">{a.lsgd_name} · {a.lsgd_type}</div>}
                </td>
                <td className="px-6 py-3 text-xs">
                  <div className="inline-flex items-center gap-1"><MapPin size={11}/>{a.district || "—"}, {a.state || "—"}</div>
                </td>
                <td className="px-6 py-3 text-xs">
                  {a.contact_person && <div>{a.contact_person}</div>}
                  {a.phone && <div className="inline-flex items-center gap-1 font-mono text-[#5C5A55]"><PhoneIcon size={10}/>{a.phone}</div>}
                </td>
                {kind === "disposal" && <td className="px-6 py-3 text-xs font-mono">{a.license_no || "—"}</td>}
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                    a.active ? "bg-[#DCEDD3] text-[#2D5A27]" : "bg-[#E5E2D9] text-[#5C5A55]"
                  }`}>{a.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => openEdit(a)}
                          data-testid={`agency-edit-${a.id}`}
                          className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1">
                    <PencilSimple size={12}/>Edit
                  </button>
                  <button onClick={() => remove(a)}
                          data-testid={`agency-del-${a.id}`}
                          className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                    <Trash size={12}/>Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
             onClick={() => setShowForm(false)}>
          <div onClick={e => e.stopPropagation()}
                data-testid="agency-form-modal"
                className="bg-white rounded-md w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  {editing ? "Edit agency" : "New agency"}
                </div>
                <div className="font-heading text-lg mt-1">
                  {kind === "collection" ? "Collection" : "Disposal (CBWTF)"} agency details
                </div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Agency name *" value={form.name}
                        onChange={v => setForm(f => ({ ...f, name: v }))}
                        testid="af-name"/>
                <Field label="State" value={form.state}
                        onChange={v => setForm(f => ({ ...f, state: v }))}
                        testid="af-state"/>
                <Field label="District *" value={form.district}
                        onChange={v => setForm(f => ({ ...f, district: v }))}
                        testid="af-district"/>
                <Field label="LSGD name (Panchayat/Municipality)"
                        value={form.lsgd_name}
                        onChange={v => setForm(f => ({ ...f, lsgd_name: v }))}
                        testid="af-lsgd-name"/>
                <Select label="LSGD type" value={form.lsgd_type}
                        options={[
                          { v: "panchayat", l: "Panchayat" },
                          { v: "municipality", l: "Municipality" },
                          { v: "corporation", l: "Corporation" },
                          { v: "", l: "N/A" },
                        ]}
                        onChange={v => setForm(f => ({ ...f, lsgd_type: v }))}
                        testid="af-lsgd-type"/>
                <Field label="Contact person" value={form.contact_person}
                        onChange={v => setForm(f => ({ ...f, contact_person: v }))}
                        testid="af-contact"/>
                <Field label="Phone" value={form.phone}
                        onChange={v => setForm(f => ({ ...f, phone: v }))}
                        testid="af-phone"/>
                <Field label="Email" value={form.email} type="email"
                        onChange={v => setForm(f => ({ ...f, email: v }))}
                        testid="af-email"/>
                <Field label="Logo URL (optional)" value={form.logo_url}
                        onChange={v => setForm(f => ({ ...f, logo_url: v }))}
                        testid="af-logo-url" placeholder="https://…"/>
                {kind === "disposal" && (
                  <>
                    <Field label="CPCB/SPCB Licence no." value={form.license_no}
                            onChange={v => setForm(f => ({ ...f, license_no: v }))}
                            testid="af-license-no"/>
                    <Field label="Licence valid until (YYYY-MM-DD)"
                            value={form.valid_until} type="date"
                            onChange={v => setForm(f => ({ ...f, valid_until: v }))}
                            testid="af-license-valid"/>
                  </>
                )}
              </div>
              <TextArea label="Address" value={form.address} rows={2}
                          onChange={v => setForm(f => ({ ...f, address: v }))}
                          testid="af-address"/>
              <TextArea label="Notes (internal)" value={form.notes} rows={2}
                          onChange={v => setForm(f => ({ ...f, notes: v }))}
                          testid="af-notes"/>
              <label className="inline-flex items-center gap-2 text-sm text-[#5C5A55]">
                <input type="checkbox" checked={form.active}
                        onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                        data-testid="af-active"/>
                Active (available in certificate wizard)
              </label>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E2D9]">
                <button type="button" onClick={() => setShowForm(false)}
                        className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
                <button type="submit" disabled={busy}
                        data-testid="af-submit"
                        className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium disabled:opacity-50">
                  {busy ? "Saving…" : editing ? "Save changes" : "Create agency"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <input type={type} value={value || ""} placeholder={placeholder}
              onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm focus:border-[#2D5A27] outline-none"/>
    </label>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
               data-testid={testid}
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <textarea rows={rows} value={value || ""}
                 onChange={e => onChange(e.target.value)}
                 data-testid={testid}
                 className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white text-sm focus:border-[#2D5A27] outline-none"/>
    </label>
  );
}
